import { useState, useEffect } from 'react';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';

interface User {
  id: number;
  username: string;
  email: string;
  supervisor_id: number | null;
  organisation_id?: number | null;
  organisation_name?: string;
}

interface SupervisorOption {
  id: number;
  username: string;
  email: string;
}

function SupervisorAssignment() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [supervisors, setSupervisors] = useState<SupervisorOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [assigningUser, setAssigningUser] = useState<number | null>(null);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      // Employees: all users in the active org
      const usersResponse = await apiService.get<User[]>('/users?limit=100');
      // Eligible supervisors: only users holding a timesheet.approver or timesheet.hr licence
      const approversResponse = await apiService.get<SupervisorOption[]>('/users/timesheet-approvers');

      if (usersResponse.success && usersResponse.data) {
        const orgId = currentUser?.organisation_id;
        const orgUsers = orgId
          ? usersResponse.data.filter(u => u.organisation_id === orgId)
          : usersResponse.data;
        setUsers(orgUsers);
      } else {
        setUsers([]);
      }

      if (approversResponse.success && approversResponse.data) {
        setSupervisors(approversResponse.data.map(u => ({
          id: u.id,
          username: u.username,
          email: u.email,
        })));
      } else {
        setSupervisors([]);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch users';
      setError(message);
      setUsers([]);
      setSupervisors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignSupervisor = async (userId: number, supervisorId: number | null) => {
    try {
      setAssigningUser(userId);
      setError(null);
      setSuccess(null);

      const response = await apiService.put<{ user: User; supervisor: User | null }>(
        `/users/${userId}/supervisor`,
        { supervisor_id: supervisorId }
      );

      if (response.success) {
        setSuccess(response.message || 'Supervisor assigned successfully');
        setUsers(prev => prev.map(u =>
          u.id === userId ? { ...u, supervisor_id: supervisorId } : u
        ));
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to assign supervisor';
      setError(message);
    } finally {
      setAssigningUser(null);
    }
  };

  const getSupervisorName = (supervisorId: number | null) => {
    if (!supervisorId) return 'None';
    const supervisor = supervisors.find(s => s.id === supervisorId);
    return supervisor ? supervisor.username : 'Unknown';
  };

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-gray-600">Loading users...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">Supervisor Assignments</h2>
        <p className="mt-1 text-sm text-gray-600">
          Assign supervisors to employees for approving hours and leave requests
        </p>
      </div>

      {error && (
        <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <p className="ml-3 text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="mx-6 mt-4 bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <p className="ml-3 text-sm text-green-800">{success}</p>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Employee
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Current Supervisor
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Assign Supervisor
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users?.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">
                  No users found
                </td>
              </tr>
            ) : (
              users?.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{user.username}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {getSupervisorName(user.supervisor_id)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={user.supervisor_id || ''}
                      onChange={(e) => {
                        e.preventDefault();
                        const value = e.target.value;
                        handleAssignSupervisor(
                          user.id,
                          value === '' ? null : parseInt(value)
                        );
                      }}
                      disabled={assigningUser === user.id}
                      className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option value="">None</option>
                      {supervisors
                        .filter(s => s.id !== user.id) // Can't supervise themselves
                        .map((supervisor) => (
                          <option key={supervisor.id} value={supervisor.id}>
                            {supervisor.username} ({supervisor.email})
                          </option>
                        ))}
                    </select>
                    {assigningUser === user.id && (
                      <div className="mt-1 flex items-center text-xs text-gray-500">
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-indigo-600 mr-1"></div>
                        Saving...
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
        <div className="flex items-start text-sm text-gray-600">
          <svg className="h-5 w-5 text-gray-400 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="font-medium text-gray-700 mb-1">
              How Supervisor Assignments Work
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {supervisors.length} eligible {supervisors.length === 1 ? 'supervisor' : 'supervisors'} found
              </span>
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>Supervisors can approve leave requests and view timesheets for their direct reports — pay frequency does not need to match</li>
              <li>Select "None" to remove a supervisor assignment</li>
              <li>Only users holding a <strong>Timesheet Approver</strong> or <strong>Timesheet HR</strong> licence appear in the dropdown — assign a seat in Subscription Management to make someone eligible</li>
              <li>Changes take effect immediately</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SupervisorAssignment;
